import { ScrollArea, Stack, Title } from '@mantine/core';

import { useTranslation } from 'react-i18next';

import { DateInput } from '../../../../../../shared/components/UI/DateInput/DateInput';
import { SidepanelHeader } from '../../../../../../shared/components/UI/Sidepanel/Sidepanel';

import { MutationFeedback } from '../../../../../../shared/components/MutationFeedback';
import { WorkSpaceUsersSelect } from '../../../../../../shared/components/WorkSpaceUsersSelect';
import { VaultPropertyType } from '../../../../../../shared/entities/VaultProperty';
import {
  useGetBidResponseByIdQuery,
  useUpdateBidResponseMutation,
} from '../../hooks/bidResponse.hook';
import { useUpdateBidResponseDocumentMutation } from '../../hooks/bidResponseDocument.hook';
import {
  useCreateBidResponseTagMutation,
  useDeleteBidResponseTagMutation,
} from '../../hooks/bidResponseTag.hook';
import { useGetVaultPropertiesQuery } from '../../hooks/vaultProperty.hook';
import { BidResponseDocuments } from './BidResponseDocuments';
import { BidResponseDeleteSection } from './DeleteSection';
import { MultiSelectProperty } from './MultiSelectProperty';
import type { BidResponseTagHandlerProps } from './SingleSelectProperty';
import { SingleSelectProperty } from './SingleSelectProperty';

const BID_RESPONSE_SIDE_PANEL_WIDTH = '50vw';
const BID_RESPONSE_SIDE_PANEL_MAX_WIDTH = 1000;

export function BidResponseSidePanel({
  bidResponseId,
  closePanel,
}: {
  bidResponseId: number;
  closePanel: () => void;
}) {
  const { t } = useTranslation('bidResponse');

  const { data: bidResponse } = useGetBidResponseByIdQuery(bidResponseId);
  const { data: vaultProperties } = useGetVaultPropertiesQuery();

  const updateBidResponseMutation = useUpdateBidResponseMutation(bidResponseId);
  const updateBidResponseDocumentMutation =
    useUpdateBidResponseDocumentMutation(bidResponseId);
  const deleteBidResponseTagMutation = useDeleteBidResponseTagMutation(
    bidResponse.id,
  );
  const createBidResponseTagMutation = useCreateBidResponseTagMutation(
    bidResponse.id,
  );

  const handleDeleteBidResponseTag = ({
    bidResponseId,
    propertyId,
    tagId,
  }: {
    bidResponseId: number;
    propertyId: number;
    tagId: number;
  }) => {
    deleteBidResponseTagMutation.mutate({
      bidResponseId,
      propertyId,
      tagId,
    });
  };

  const handleCreateBidResponseTag = ({
    bidResponseId,
    propertyId,
    optionId,
  }: BidResponseTagHandlerProps) => {
    createBidResponseTagMutation.mutate({
      bidResponseId,
      propertyId,
      payload: { vaultPropertyOptionId: optionId },
    });
  };

  return (
    <Stack h="100%" spacing={0}>
      <SidepanelHeader
        header={<Title order={4}>{bidResponse.title}</Title>}
        closePanel={closePanel}
      >
        <MutationFeedback
          mutations={[
            updateBidResponseMutation,
            updateBidResponseDocumentMutation,
            deleteBidResponseTagMutation,
            createBidResponseTagMutation,
          ]}
        />
      </SidepanelHeader>

      <ScrollArea h="100%" sx={{ flexGrow: 1 }}>
        <Stack
          px="03"
          py="02"
          mb="05"
          spacing="02"
          w={BID_RESPONSE_SIDE_PANEL_WIDTH}
          maw={BID_RESPONSE_SIDE_PANEL_MAX_WIDTH}
        >
          <Stack px="03" py="02" spacing="02">
            <WorkSpaceUsersSelect
              required
              user={bidResponse.owner}
              label={t('vault.input.owner.label')}
              placeholder={t('vault.input.owner.placeholder')}
              onChange={value => {
                if (!value) return;

                updateBidResponseMutation.mutate({
                  ownerId: Number(value),
                });
              }}
            />

            <DateInput
              label={t('vault.input.responseDeadline.label')}
              placeholder={t('vault.input.responseDeadline.placeholder')}
              required
              value={new Date(bidResponse.responseDeadline)}
              onChange={value => {
                updateBidResponseMutation.mutate({
                  responseDeadline: value as Date,
                });
              }}
            />

            {vaultProperties.map(vaultProperty =>
              vaultProperty.type === VaultPropertyType.MULTISELECT ? (
                <MultiSelectProperty
                  key={vaultProperty.id}
                  bidResponse={bidResponse}
                  vaultProperty={vaultProperty}
                  deleteBidResponseTag={handleDeleteBidResponseTag}
                  createBidResponseTag={handleCreateBidResponseTag}
                />
              ) : (
                <SingleSelectProperty
                  key={vaultProperty.id}
                  bidResponse={bidResponse}
                  vaultProperty={vaultProperty}
                  deleteBidResponseTag={handleDeleteBidResponseTag}
                  createBidResponseTag={handleCreateBidResponseTag}
                />
              ),
            )}
          </Stack>
          <BidResponseDocuments bidResponseId={bidResponseId} />
          <BidResponseDeleteSection
            bidResponseId={bidResponse.id}
            onDelete={closePanel}
          />
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
